// src/pages/admin/EstimatesOverview.js
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../lib/firebase";

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function getMillis(ts) {
  if (!ts) return 0;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.getTime();
}

function formatAddress(addr) {
  if (!addr) return "-";
  const parts = [
    addr.line1,
    addr.line2,
    addr.city,
    addr.region,
    addr.postalCode,
    addr.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
}

export default function EstimatesOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const ref = collection(db, "quotes");
    const qy = query(ref, orderBy("createdAt", "desc"), limit(500));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(data);
        setLoading(false);
      },
      () => {
        setRows([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let data = rows.filter((r) => r.docType === "estimate");

    if (needle) {
      data = data.filter((r) => {
        const blob = [r.clientName, r.clientOrganization]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(needle);
      });
    }

    data.sort((a, b) => getMillis(b.createdAt) - getMillis(a.createdAt));
    return data;
  }, [rows, q]);

  const active = filtered.filter((r) => !r.archived);
  const archived = filtered.filter((r) => r.archived);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>Estimates</h2>
          <div className="micro muted">
            {loading ? "Loading..." : `${active.length} active`}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search client or organization"
            style={{
              width: 320,
              maxWidth: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.75)",
            }}
          />

          <Link className="btn" to="/admin">
            Back
          </Link>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        {loading && <p className="muted">Loading estimates...</p>}

        {!loading && active.length === 0 && (
          <p className="muted">No active estimates yet.</p>
        )}

        {!loading && active.length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            {active.map((r) => (
              <article
                key={r.id}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 14,
                  padding: 14,
                  background: "rgba(255,255,255,0.7)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ minWidth: 260 }}>
                    <strong style={{ fontSize: 16 }}>{r.clientName || "Client"}</strong>
                    {r.clientOrganization && (
                      <div className="micro muted" style={{ marginTop: 4 }}>
                        {r.clientOrganization}
                      </div>
                    )}
                    <div className="micro muted" style={{ marginTop: 4 }}>
                      {formatAddress(r.clientAddress)}
                    </div>
                    <div className="micro muted" style={{ marginTop: 6 }}>
                      Total: <strong>{money(r?.totals?.grandTotal ?? 0)}</strong>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Link className="btn" to={`/admin/estimates/${r.id}`}>
                      View
                    </Link>
                    <Link className="btn primary" to={`/admin/estimates/${r.id}/edit`}>
                      Edit
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <button className="btn" type="button" onClick={() => setShowArchived((p) => !p)}>
            Archived estimates ({archived.length})
          </button>

          {showArchived && (
            <div style={{ marginTop: 12 }}>
              {archived.length === 0 && <p className="muted">No archived estimates.</p>}

              {archived.length > 0 && (
                <div style={{ display: "grid", gap: 12 }}>
                  {archived.map((r) => (
                    <article
                      key={r.id}
                      style={{
                        border: "1px solid rgba(0,0,0,0.1)",
                        borderRadius: 14,
                        padding: 14,
                        background: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ minWidth: 260 }}>
                          <strong style={{ fontSize: 16 }}>{r.clientName || "Client"}</strong>
                          {r.clientOrganization && (
                            <div className="micro muted" style={{ marginTop: 4 }}>
                              {r.clientOrganization}
                            </div>
                          )}
                          <div className="micro muted" style={{ marginTop: 4 }}>
                            {formatAddress(r.clientAddress)}
                          </div>
                          <div className="micro muted" style={{ marginTop: 6 }}>
                            Total: <strong>{money(r?.totals?.grandTotal ?? 0)}</strong>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <Link className="btn" to={`/admin/estimates/${r.id}`}>
                            View
                          </Link>
                          <Link className="btn primary" to={`/admin/estimates/${r.id}/edit`}>
                            Edit
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
